/**
 * src/services/api.js
 * Central HTTP client. Handles JWT auth, auto-refresh, v2 envelope, timeouts.
 */

const BASE_URL    = process.env.REACT_APP_API_URL     || 'http://localhost:8000/api/v1';

// ── Simple frontend response cache (stale-while-revalidate) ──────────────────
// Caches GET responses for 3 minutes — page feels instant on revisit
const _cache    = new Map();
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes in ms

function cacheGet(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) { _cache.delete(key); return null; }
  return entry.data;
}
function cacheSet(key, data) { _cache.set(key, { data, ts: Date.now() }); }
export function cacheClear()  { _cache.clear(); }
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const TIMEOUT_MS  = 120000; // 120s — allows for order placement + email sending on Render
const FALLBACK_IMG = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&q=80';

// ── ApiError ──────────────────────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(code, message, detail = null, status = 0) {
    super(message);
    this.name   = 'ApiError';
    this.code   = code;
    this.detail = detail;
    this.status = status;
  }
}

export const ErrorCode = {
  NETWORK:          'NETWORK_ERROR',
  TIMEOUT:          'REQUEST_TIMEOUT',
  NOT_AUTH:         'NOT_AUTHENTICATED',
  PERMISSION:       'PERMISSION_DENIED',
  NOT_FOUND:        'NOT_FOUND',
  VALIDATION:       'VALIDATION_ERROR',
  THROTTLED:        'RATE_LIMIT_EXCEEDED',
  PRODUCT_OOS:      'PRODUCT_OUT_OF_STOCK',
  COUPON_EXPIRED:   'COUPON_EXPIRED',
  COUPON_LIMIT:     'COUPON_LIMIT_REACHED',
  COUPON_MIN_ORDER: 'COUPON_MIN_ORDER',
  INVALID_STATUS:   'INVALID_ORDER_STATUS',
  SERVER:           'INTERNAL_SERVER_ERROR',
};

// ── Token management ──────────────────────────────────────────────────────────
let _refreshPromise = null;

const getAccessToken  = () => localStorage.getItem('lc_access');
const getRefreshToken = () => localStorage.getItem('lc_refresh');

function setTokens(access, refresh) {
  if (access)  localStorage.setItem('lc_access',  access);
  if (refresh) localStorage.setItem('lc_refresh', refresh);
}

function clearTokens() {
  localStorage.removeItem('lc_access');
  localStorage.removeItem('lc_refresh');
}

async function tryRefreshToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    const refresh = getRefreshToken();
    if (!refresh) return false;
    try {
      const res = await fetch(`${BASE_URL}/users/token/refresh/`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refresh }),
      });
      if (!res.ok) return false;
      let json;
      try { json = await res.json(); } catch { return false; }
      const access = json?.data?.access || json?.access;
      if (!access) return false;
      setTokens(access, null);
      return true;
    } catch {
      return false;
    }
  })();
  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

// ── Fetch with timeout ────────────────────────────────────────────────────────
async function fetchWithTimeout(url, config) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...config, signal: ctrl.signal });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new ApiError(ErrorCode.TIMEOUT, `Request timed out after ${TIMEOUT_MS/1000}s.`, null, 408);
    }
    throw new ApiError(ErrorCode.NETWORK, 'Cannot connect to server. Check your connection.', null, 0);
  } finally {
    clearTimeout(timer);
  }
}

// ── Parse response ────────────────────────────────────────────────────────────
async function parseResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    throw new ApiError(ErrorCode.SERVER, `Unexpected response (HTTP ${res.status}).`, null, res.status);
  }
  try {
    return await res.json();
  } catch {
    throw new ApiError(ErrorCode.SERVER, 'Could not parse server response.', null, res.status);
  }
}

function throwFromJson(json, status) {
  if (json?.error) {
    throw new ApiError(json.error.code || 'API_ERROR', json.error.message || `Error (${status})`, json.error.detail || null, status);
  }
  if (json?.detail) {
    throw new ApiError('API_ERROR', json.detail, null, status);
  }
  if (typeof json === 'object' && json !== null) {
    const k = Object.keys(json)[0];
    const v = Array.isArray(json[k]) ? json[k][0] : json[k];
    throw new ApiError(ErrorCode.VALIDATION, String(v || `Error (${status})`), json, status);
  }
  throw new ApiError('API_ERROR', `Error (${status})`, null, status);
}

// ── Core request ──────────────────────────────────────────────────────────────
async function request(method, path, body = null) {
  const buildHeaders = () => ({
    'Content-Type': 'application/json',
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  });

  const config = { method, headers: buildHeaders(), body: body ? JSON.stringify(body) : undefined };

  let res = await fetchWithTimeout(`${BASE_URL}${path}`, config);

  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      config.headers = buildHeaders();
      res = await fetchWithTimeout(`${BASE_URL}${path}`, config);
    } else {
      clearTokens();
      window.dispatchEvent(new CustomEvent('lc:session-expired'));
      throw new ApiError(ErrorCode.NOT_AUTH, 'Session expired. Please log in again.', null, 401);
    }
  }

  if (res.status === 204) return null;

  const json = await parseResponse(res);
  if (!res.ok) throwFromJson(json, res.status);
  if (json?.success === false && json?.error) throwFromJson(json, res.status);
  return json;
}

const get = (path) => request('GET', path);

// Cached version of GET — used only for public product/category endpoints
// Returns stale data instantly while refreshing in background
function cachedGet(path) {
  const cached = cacheGet(path);
  const fetchFresh = () => request('GET', path).then(data => { cacheSet(path, data); return data; }).catch(e => { throw e; });
  if (cached) {
    fetchFresh().catch(() => {}); // silent background refresh
    return Promise.resolve(cached);
  }
  return fetchFresh();
}
const post  = (path, body)  => request('POST',   path, body);
const put   = (path, body)  => request('PUT',    path, body);
const patch = (path, body)  => request('PATCH',  path, body);
const del   = (path)        => request('DELETE', path);

// Multipart request for file uploads (no Content-Type header — browser sets boundary)
async function requestFormData(method, path, formData) {
  const headers = { ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}) };
  const res = await fetch(`${BASE_URL}${path}`, { method, headers, body: formData });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError(
      json.code || 'ERROR', json.message || 'Request failed',
      json.detail || null, res.status
    );
  }
  return res.json();
}
const postFormData  = (path, fd) => requestFormData('POST',  path, fd);
const patchFormData = (path, fd) => requestFormData('PATCH', path, fd);

// ── Envelope unwrappers ───────────────────────────────────────────────────────
export function unwrap(response) {
  if (response != null && typeof response === 'object' && 'data' in response) {
    return response.data;
  }
  return response;
}

export function unwrapPage(response) {
  if (response != null && typeof response === 'object' && 'data' in response) {
    return {
      data:     Array.isArray(response.data) ? response.data : [],
      count:    response.count    ?? 0,
      next:     response.next     ?? null,
      previous: response.previous ?? null,
    };
  }
  return { data: Array.isArray(response) ? response : [], count: 0, next: null, previous: null };
}

export function getErrorMessage(err) {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error)    return err.message;
  return 'An unexpected error occurred.';
}

export function getFieldErrors(err) {
  if (!(err instanceof ApiError) || !err.detail || typeof err.detail !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(err.detail)) {
    out[k] = Array.isArray(v) ? v[0] : String(v);
  }
  return out;
}

// ── Image URL helper ──────────────────────────────────────────────────────────
export function getImageUrl(product) {
  if (!product) return FALLBACK_IMG;
  if (product.image) {
    // Backend now returns absolute URLs (http://...) — use directly.
    // Fallback: if still a relative path, prepend BACKEND_URL + /media/
    const img = product.image;
    if (img.startsWith('http://') || img.startsWith('https://')) return img;
    // Relative path like "products/file.jpg" → prepend /media/
    return `${BACKEND_URL}/media/${img.replace(/^\//, '')}`;
  }
  if (product.image_url) return product.image_url;
  return FALLBACK_IMG;
}

export function getImageUrlFallback() {
  return FALLBACK_IMG;
}

// ── Query string builder ──────────────────────────────────────────────────────
function buildQS(params = {}) {
  return new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null && v !== false))
  ).toString();
}

// ── API namespaces ────────────────────────────────────────────────────────────
export const authApi = {
  register:       (data) => post('/users/register/',         data),
  login:          (data) => post('/users/login/',            data),
  profile:        ()     => get('/users/profile/'),
  update:         (data) => patch('/users/profile/',         data),
  changePassword: (data) => patch('/users/password/change/', data),
};

export const productsApi = {
  list:       (params = {}) => { const qs = buildQS(params); return cachedGet(`/products/${qs ? `?${qs}` : ''}`); },
  featured:   ()            => cachedGet('/products/featured/'),
  detail:     (slug)        => get(`/products/${slug}/`),
  categories: ()            => cachedGet('/products/categories/'),

  adminList:           (params = {}) => { const qs = buildQS(params); return get(`/products/admin/${qs ? `?${qs}` : ''}`); },
  adminCreate:         (data)        => post('/products/admin/',                  data),
  adminCreateFormData: (fd)          => postFormData('/products/admin/',          fd),
  adminImport:         (fd)          => postFormData('/products/admin/import/',   fd),
  adminPatchFormData:  (id, fd)      => patchFormData(`/products/admin/${id}/`,  fd),
  adminUpdate:         (id, data)    => put(`/products/admin/${id}/`,             data),
  adminPatch:          (id, data)    => patch(`/products/admin/${id}/`,           data),
  adminDelete:         (id)          => del(`/products/admin/${id}/`),
  adminHardDelete:     (id)          => del(`/products/admin/${id}/?permanent=true`),
  adminBulk:           (data)        => post('/products/admin/bulk/', data),
  adminUpdateStock:    (id, stock)   => patch(`/products/admin/${id}/stock/`,     { stock }),
  adminCategories:     ()            => get('/products/admin/categories/'),
  adminCreateCategory: (data)        => post('/products/admin/categories/',       data),
  adminUpdateCategory: (id, data)    => put(`/products/admin/categories/${id}/`,  data),
  adminDeleteCategory: (id)          => del(`/products/admin/categories/${id}/`),
};

export const ordersApi = {
  place:          (data)        => post('/orders/place/',           data),
  validateCoupon: (data)        => post('/orders/coupon/validate/', data),
  myOrders:       ()            => get('/orders/my/'),
  detail:         (id)          => get(`/orders/${id}/`),

  adminList:         (params = {}) => { const qs = buildQS(params); return get(`/orders/admin/${qs ? `?${qs}` : ''}`); },
  adminUpdate:       (id, data)    => patch(`/orders/admin/${id}/`,        data),
  adminCoupons:      ()            => get('/orders/admin/coupons/'),
  adminCreateCoupon: (data)        => post('/orders/admin/coupons/',        data),
  adminUpdateCoupon: (id, data)    => patch(`/orders/admin/coupons/${id}/`, data),
  adminDeleteCoupon: (id)          => del(`/orders/admin/coupons/${id}/`),
  adminBulkOrders:   (data)        => post('/orders/admin/bulk/', data),
  trackOrder:        (data)        => post('/orders/track/', data),
};

export const coreApi = {
  health:    () => get('/core/health/'),
  dashboard: () => get('/core/dashboard/'),
};
